import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { works } from "@/mocks/works";
import Tag from "@/components/base/Tag";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButtons from "@/components/feature/ShareButtons";
import BrowseHistoryRail from "@/components/feature/BrowseHistoryRail";
import { useSEO } from "@/hooks/useSEO";
import { addHistory } from "@/hooks/useBrowseHistory";

export default function WorkDetail() {
  const { id } = useParams<{ id: string }>();
  const work = works.find((w) => w.id === id);

  const related = useMemo(() => {
    if (!work) return [];
    return works
      .filter((w) => w.id !== work.id && (w.genre.some((g) => work.genre.includes(g)) || w.type === work.type))
      .sort((a, b) => b.heat - a.heat)
      .slice(0, 4);
  }, [work]);

  useSEO({
    title: work ? `${work.title}详情 - 朝桐光` : "作品详情 - 朝桐光",
    description: work
      ? `${work.title}（${work.year}年 ${work.type}）由${work.director}执导，${work.cast.join("、")}主演，朝桐光出品。${work.synopsis.slice(0, 60)}`
      : "朝桐光影视作品详情页。",
    keywords: work
      ? `${work.title},朝桐光,${work.type},${work.genre.join(",")},${work.cast.join(",")}`
      : "朝桐光,影视作品",
  });

  useEffect(() => {
    if (!work) return;
    addHistory({
      type: "work",
      id: work.id,
      title: work.title,
      subtitle: `${work.year} · ${work.type} · ${work.genre.join(" / ")}`,
      cover: work.cover,
      path: `/works/${work.id}`,
    });
  }, [work]);

  if (!work) {
    return (
      <div className="mx-auto flex w-full max-w-page flex-col items-center px-4 py-32 text-center md:px-6">
        <h1 className="text-2xl font-bold text-foreground-950">未找到该作品</h1>
        <p className="mt-3 text-sm text-foreground-600">该作品可能已下线或链接有误。</p>
        <Link
          to="/works"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回影视作品
        </Link>
      </div>
    );
  }

  const metaRows = [
    { label: "上映年份", value: `${work.year} 年` },
    { label: "作品类型", value: work.type },
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "题材", value: work.genre.join(" / ") },
    { label: "时长", value: work.duration },
    { label: "地区", value: work.region },
    { label: "状态", value: work.status },
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={work.still}
            alt={`${work.title} 剧照`}
            title={`${work.title} - 朝桐光影视作品`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/50" />
        </div>
        <div className="relative mx-auto w-full max-w-page px-4 pb-12 pt-28 md:px-6 md:pb-16 md:pt-36">
          <div className="mb-5 [&_a]:text-background-300 [&_span]:text-background-100 [&_i]:text-background-400">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视作品", to: "/works" }, { label: work.title }]} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Tag tone="accent">{work.type}</Tag>
            <Tag tone="secondary">{work.status}</Tag>
            {work.genre.map((g) => (
              <span key={g} className="rounded-full border border-background-50/25 px-3 py-0.5 text-xs text-background-200">
                {g}
              </span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {work.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-background-200">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-400"></i>
              <strong className="font-heading text-xl font-black text-background-50">{work.rating.toFixed(1)}</strong>
              评分
            </span>
            <span>{work.year} · {work.region}</span>
            <span>导演 {work.director}</span>
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-page grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex flex-col gap-6 sm:flex-row">
            <div className="w-full shrink-0 sm:w-48">
              <div className="w-full overflow-hidden rounded-lg" style={{ aspectRatio: "2 / 3" }}>
                <img
                  src={work.cover}
                  alt={`${work.title} 海报`}
                  title={`${work.title} - 朝桐光影视作品`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-file-text-line"></i>
                </span>
                作品简介
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">{work.synopsis}</p>
              <div className="mt-6">
                <ShareButtons title={`${work.title} - 朝桐光`} />
              </div>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                <i className="ri-information-line"></i>
              </span>
              详细资料
            </h2>
            <dl className="mt-4 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              {metaRows.map((row) => (
                <div key={row.label} className="flex gap-4 border-b border-background-200 py-3">
                  <dt className="w-20 shrink-0 text-sm text-foreground-500">{row.label}</dt>
                  <dd className="text-sm font-medium text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                <i className="ri-folder-line"></i>
              </span>
              所属分类
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/works"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 text-sm font-medium text-primary-700 hover:bg-primary-200"
              >
                <i className="ri-film-line"></i>
                全部影视作品
              </Link>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-800">{work.type}</span>
              {work.genre.map((g) => (
                <span key={g} className="rounded-full bg-secondary-100 px-3 py-1 text-sm text-secondary-800">
                  {g}
                </span>
              ))}
            </div>
          </div>
        </div>

        <aside className="flex flex-col gap-6 lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-movie-2-line"></i>
              </span>
              相关推荐
            </h3>
            <ul className="mt-4 space-y-3">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/works/${r.id}`}
                    className="group flex items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                  >
                    <img
                      src={r.cover}
                      alt={r.title}
                      className="h-16 w-11 shrink-0 rounded-md object-cover object-top"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                        {r.title}
                      </span>
                      <span className="block truncate text-xs text-foreground-500">
                        {r.year} · {r.type}
                      </span>
                      <span className="mt-1 block text-xs text-accent-600">评分 {r.rating.toFixed(1)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <BrowseHistoryRail />
        </aside>
      </div>
    </div>
  );
}